import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { grey, primary, success, warning, error as errorColor, info } from '../../../styles/tokens';

/**
 * Liste des Tenants (Admin)
 * Affiche les bases de donnees des coachs avec statistiques
 */
const TenantList = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchTenants = async () => {
    try {
      setLoading(true);
      setError(null);

      // On recupere les coaches qui ont des tenants
      let url = `/admin/coaches?page=${pagination.page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await api.get(url);

      // Transformer les coaches en donnees tenant
      const tenantData = (response.coaches || []).map(coach => ({
        _id: coach._id,
        tenantId: coach.tenantId,
        coachName: `${coach.firstName} ${coach.lastName}`,
        coachEmail: coach.email,
        status: coach.tenantStatus,
        plan: coach.subscriptionPlan,
        currentClients: coach.currentClients || 0,
        maxClients: coach.maxClients || 0,
        createdAt: coach.createdAt,
      }));

      setTenants(tenantData);
      setPagination(prev => ({
        ...prev,
        pages: response.pagination?.pages || 1,
        total: response.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Erreur lors du chargement des tenants:', err);
      setError('Impossible de charger la liste des tenants.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, statusFilter]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTenants();
    }
  };

  const getStatusChipProps = (status) => {
    const configs = {
      active: { label: 'Actif', sx: { bgcolor: success[50], color: success.dark } },
      suspended: { label: 'Suspendu', sx: { bgcolor: errorColor[50], color: errorColor.dark } },
      pending: { label: 'En attente', sx: { bgcolor: warning[50], color: warning.dark } },
      provisioning: { label: 'Provisioning', sx: { bgcolor: info[50], color: info.dark } },
    };
    return configs[status] || { label: status || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getPlanChipProps = (plan) => {
    const configs = {
      starter: { label: 'Starter', sx: { bgcolor: grey[100], color: grey[700] } },
      pro: { label: 'Pro', sx: { bgcolor: primary[50], color: primary[600] } },
      enterprise: { label: 'Enterprise', sx: { bgcolor: success[50], color: success.dark } },
    };
    return configs[plan] || { label: plan || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getUsagePercentage = (current, max) => {
    if (!max || max === 0) return 0;
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return errorColor.main;
    if (percentage >= 70) return warning.main;
    return success.main;
  };

  // Stats agregees
  const stats = {
    total: tenants.length,
    active: tenants.filter(t => t.status === 'active').length,
    suspended: tenants.filter(t => t.status === 'suspended').length,
  };

  return (
    <>
      <Helmet>
        <title>Gestion des Tenants - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h1">
            Tenants
          </Typography>
          <Button
            startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
            onClick={fetchTenants}
            disabled={loading}
          >
            Actualiser
          </Button>
        </Stack>

        {/* Stats Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: primary[50], width: 40, height: 40 }}>
                  <StorageIcon sx={{ color: primary[500], fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="h2">{stats.total}</Typography>
                  <Typography variant="caption">Total tenants</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: success[50], width: 40, height: 40 }}>
                  <StorageIcon sx={{ color: success.main, fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="h2">{stats.active}</Typography>
                  <Typography variant="caption">Actifs</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: errorColor[50], width: 40, height: 40 }}>
                  <StorageIcon sx={{ color: errorColor.main, fontSize: 20 }} />
                </Avatar>
                <Box>
                  <Typography variant="h2">{stats.suspended}</Typography>
                  <Typography variant="caption">Suspendus</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                placeholder="Rechercher par nom ou tenant ID..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearch}
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="suspended">Suspendu</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Liste */}
        <Card>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : tenants.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun tenant trouve.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Coach</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Utilisation</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Creation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => {
                    const statusProps = getStatusChipProps(tenant.status);
                    const planProps = getPlanChipProps(tenant.plan);
                    const usagePercent = getUsagePercentage(tenant.currentClients, tenant.maxClients);
                    const usageColor = getUsageColor(usagePercent);

                    return (
                      <TableRow key={tenant._id}>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                          >
                            {tenant.tenantId || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {tenant.coachName}
                            </Typography>
                            <Typography variant="caption">
                              {tenant.coachEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planProps.label}
                            size="small"
                            sx={planProps.sx}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Box>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                              <Typography variant="caption">
                                {tenant.currentClients} / {tenant.maxClients || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: usageColor }}>
                                {usagePercent.toFixed(0)}%
                              </Typography>
                            </Stack>
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: grey[100],
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: usageColor,
                                  borderRadius: 3,
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusProps.label}
                            size="small"
                            sx={statusProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {new Date(tenant.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default TenantList;
